import { SHIPPO_LABEL_URL } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import axios from 'axios';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { useEffect } from 'react';
import type { LabelInformationProps } from '../ui/returnStatusTab/labelInformation/labelInformation.types';

/**
 * Loads and downloads the Shippo return label PDF shown in a study's return status tab.
 * Fetches the label PDF through the proxy whenever the transaction's labelUrl changes and keeps
 * the buffer and loading flags in state for the download link.
 *
 * @param props - Label props carrying the shipping transaction.
 * @returns The state and setter, the PDF fetcher and the download handler.
 */
export const useLabelInformation = ({
  transaction,
}: Pick<LabelInformationProps, 'transaction'>) => {
  const [state, setState] = useSetState<any>({
    isLoadingPDF: false,
    filePDF: '',
    isRefreshingTracking: false,
  });

  /**
   * Downloads a Shippo return-label PDF through the proxy so it can be saved locally. Skips an empty url, rebuilds the path onto the SHIPPO_LABEL_URL base with the original host sent as a target header, requests it as an arraybuffer into state, and reports a failure as an error message while clearing the loading flag.
   *
   * @param url - Original Shippo label url.
   * @returns A promise resolving once the PDF buffer and the loading flag have been stored in state.
   */
  const fetchFilePDF = async (url: any) => {
    if (!url) {
      return;
    }
    try {
      setState({ isLoadingPDF: true });
      const splitString = _.split(url, '.com/');
      const newURL = `${SHIPPO_LABEL_URL}${splitString[1]}`;
      const response = await axios.get(newURL, {
        responseType: 'arraybuffer',
        headers: {
          target: `${splitString[0]}.com/`,
        },
      });
      setState({ isLoadingPDF: false, filePDF: response.data });
    } catch (error) {
      setState({ isLoadingPDF: false });
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Saves the already-fetched return-label PDF under the given filename. Does nothing while the PDF is still downloading, otherwise hands the buffered file to the browser download.
   *
   * @param filename - Name for the saved file.
   * @param _index - Index of the clicked link; unused.
   * @returns A promise resolving once the browser download has been triggered.
   */
  const onDownloadLink = async (filename: any, _index: any) => {
    if (state.isLoadingPDF) {
      return;
    }
    fileDownload(state.filePDF, filename);
  };

  useEffect(() => {
    fetchFilePDF(transaction?.labelUrl);
  }, [transaction?.labelUrl]);

  return { state, setState, fetchFilePDF, onDownloadLink };
};
